import { useEffect, useRef, memo } from "react";
import { useFx } from "@/lib/fx/fxTier";

/**
 * Fundo estelar dos menus.
 *
 * Estratégia de custo: o campo de estrelas é estático, então é
 * rasterizado UMA vez num canvas offscreen e depois só copiado com
 * drawImage. Por frame animamos apenas (a) um parallax de poucos
 * pixels nas duas camadas, (b) uma camada fina de estrelas que
 * cintilam, e (c) os cometas. Isso troca ~500 arc()+fill() por
 * frame por 2 drawImage + ~40 arcs.
 *
 * Uma única instância deve existir na árvore (montada em Game.tsx,
 * acima do switch de views) — assim navegar entre menus não reseta
 * o céu nem realoca nada.
 */

interface Star {
  x: number;
  y: number;
  size: number;
  speed: number;
  twinklePhase: number;
  brightness: number;
}

interface Planet {
  x: number;
  y: number;
  size: number;
  color: string;
  orbitRadius: number;
  orbitSpeed: number;
  angle: number;
}

interface Comet {
  x: number;
  y: number;
  speed: number;
  angle: number;
  size: number;
  tailLength: number;
}

type Tier = "low" | "medium" | "high";

interface TierBudget {
  /** Divisor de área: menor = mais estrelas. */
  starDensity: number;
  /** Fração das estrelas que cintila (as demais ficam no bitmap estático). */
  twinkleRatio: number;
  maxComets: number;
  targetFps: number;
  /** Teto de devicePixelRatio — retina completo é caro e imperceptível aqui. */
  maxDpr: number;
  parallax: boolean;
  nebula: boolean;
}

const BUDGETS: Record<Tier, TierBudget> = {
  low: { starDensity: 14000, twinkleRatio: 0, maxComets: 0, targetFps: 24, maxDpr: 1, parallax: false, nebula: false },
  medium: { starDensity: 7000, twinkleRatio: 0.12, maxComets: 2, targetFps: 30, maxDpr: 1.5, parallax: true, nebula: false },
  high: { starDensity: 4000, twinkleRatio: 0.18, maxComets: 4, targetFps: 60, maxDpr: 2, parallax: true, nebula: true },
};

export const SpaceBackground = memo(function SpaceBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tier = useFx((s) => s.tier);
  const reducedMotion = useFx((s) => s.reducedMotion);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    const budget = BUDGETS[tier];
    const frameInterval = 1000 / budget.targetFps;

    let width = 0;
    let height = 0;
    let dpr = 1;

    // Camada estática pré-rasterizada (estrelas fixas + nebulosa)
    let staticLayer: HTMLCanvasElement | null = null;
    let twinklers: Star[] = [];
    let planets: Planet[] = [];
    let comets: Comet[] = [];
    let animationId = 0;
    let running = true;
    let lastFrameTime = 0;

    // Alvo de parallax seguido suavemente — reage ao ponteiro sem custo real
    const pointer = { x: 0.5, y: 0.5 };
    const parallax = { x: 0, y: 0 };

    const buildStaticLayer = () => {
      const layer = document.createElement("canvas");
      layer.width = canvas.width;
      layer.height = canvas.height;
      const lctx = layer.getContext("2d");
      if (!lctx) return null;

      lctx.scale(dpr, dpr);
      lctx.fillStyle = "#070a16";
      lctx.fillRect(0, 0, width, height);

      // Nebulosa: dois gradientes radiais amplos. Custo zero por frame
      // porque nunca mais é redesenhada.
      if (budget.nebula) {
        const nebulaSpots: Array<[number, number, number, string]> = [
          [width * 0.2, height * 0.3, Math.max(width, height) * 0.45, "rgba(88, 28, 135, 0.22)"],
          [width * 0.8, height * 0.75, Math.max(width, height) * 0.4, "rgba(14, 116, 144, 0.18)"],
        ];
        for (const [nx, ny, nr, color] of nebulaSpots) {
          const grad = lctx.createRadialGradient(nx, ny, 0, nx, ny, nr);
          grad.addColorStop(0, color);
          grad.addColorStop(1, "rgba(7, 10, 22, 0)");
          lctx.fillStyle = grad;
          lctx.fillRect(0, 0, width, height);
        }
      }

      const total = Math.floor((width * height) / budget.starDensity);
      const twinkleCount = Math.floor(total * budget.twinkleRatio);
      twinklers = [];

      for (let i = 0; i < total; i++) {
        const star: Star = {
          x: Math.random() * width,
          y: Math.random() * height,
          size: Math.random() * 1.6 + 0.4,
          speed: Math.random() * 0.02 + 0.008,
          twinklePhase: Math.random() * Math.PI * 2,
          brightness: Math.random() * 0.25 + 0.65,
        };

        if (i < twinkleCount && !reducedMotion) {
          // Estrelas que piscam ficam de fora do bitmap e são desenhadas por frame
          twinklers.push(star);
        } else {
          lctx.fillStyle = `rgba(255, 255, 255, ${star.brightness})`;
          lctx.beginPath();
          lctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
          lctx.fill();
        }
      }

      return layer;
    };

    const buildPlanets = () => {
      planets = [
        { x: width * 0.15, y: height * 0.25, size: 60, color: "#8b5cf6", orbitRadius: 30, orbitSpeed: 0.0008, angle: 0 },
        { x: width * 0.85, y: height * 0.7, size: 45, color: "#06b6d4", orbitRadius: 20, orbitSpeed: 0.0012, angle: Math.PI },
        { x: width * 0.7, y: height * 0.15, size: 35, color: "#f59e0b", orbitRadius: 15, orbitSpeed: 0.001, angle: Math.PI / 2 },
      ];
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, budget.maxDpr);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      staticLayer = buildStaticLayer();
      buildPlanets();
      comets = [];
    };

    const createComet = (): Comet => {
      const fromTop = Math.random() < 0.6;
      return {
        x: fromTop ? Math.random() * width : width + 20,
        y: fromTop ? -20 : Math.random() * height * 0.6,
        speed: Math.random() * 3 + 2,
        angle: fromTop ? Math.PI * 0.25 + Math.random() * Math.PI * 0.35 : Math.PI * 0.6 + Math.random() * Math.PI * 0.3,
        size: Math.random() * 2 + 1,
        tailLength: Math.random() * 60 + 40,
      };
    };

    const draw = (currentTime: number) => {
      if (!running) return;

      const elapsed = currentTime - lastFrameTime;
      if (elapsed < frameInterval) {
        animationId = requestAnimationFrame(draw);
        return;
      }
      lastFrameTime = currentTime - (elapsed % frameInterval);

      // Parallax suavizado (lerp) — desloca no máximo 12px, o que
      // basta para dar profundidade sem revelar as bordas do bitmap.
      if (budget.parallax && !reducedMotion) {
        parallax.x += ((pointer.x - 0.5) * 12 - parallax.x) * 0.04;
        parallax.y += ((pointer.y - 0.5) * 12 - parallax.y) * 0.04;
      }

      ctx.fillStyle = "#070a16";
      ctx.fillRect(0, 0, width, height);

      if (staticLayer) {
        ctx.drawImage(staticLayer, parallax.x, parallax.y, width, height);
      }

      // Estrelas que cintilam — poucas, e só quando há movimento
      if (twinklers.length > 0) {
        for (const star of twinklers) {
          star.twinklePhase += star.speed;
          const twinkle = 0.6 + (Math.sin(star.twinklePhase) + 1) * 0.2;
          ctx.fillStyle = `rgba(255, 255, 255, ${star.brightness * twinkle})`;
          ctx.beginPath();
          ctx.arc(star.x + parallax.x, star.y + parallax.y, star.size, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Planetas — parallax em contra-fase (camada "mais próxima")
      const planetShiftX = parallax.x * 2;
      const planetShiftY = parallax.y * 2;
      for (const planet of planets) {
        if (!reducedMotion) planet.angle += planet.orbitSpeed;
        const px = planet.x + Math.cos(planet.angle) * planet.orbitRadius + planetShiftX;
        const py = planet.y + Math.sin(planet.angle) * planet.orbitRadius * 0.5 + planetShiftY;

        const gradient = ctx.createRadialGradient(px, py, 0, px, py, planet.size);
        gradient.addColorStop(0, planet.color);
        gradient.addColorStop(0.7, planet.color);
        gradient.addColorStop(1, planet.color + "00");
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(px, py, planet.size, 0, Math.PI * 2);
        ctx.fill();

        // Halo por gradiente em vez de shadowBlur (blur por frame em
        // Canvas 2D é caro, sobretudo em WebView mobile)
        const halo = ctx.createRadialGradient(px, py, planet.size * 0.6, px, py, planet.size * 1.6);
        halo.addColorStop(0, planet.color + "55");
        halo.addColorStop(1, planet.color + "00");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(px, py, planet.size * 1.6, 0, Math.PI * 2);
        ctx.fill();
      }

      // Cometas
      if (budget.maxComets > 0 && !reducedMotion) {
        const alive: Comet[] = [];
        for (const comet of comets) {
          comet.x += Math.cos(comet.angle) * comet.speed;
          comet.y += Math.sin(comet.angle) * comet.speed;

          if (comet.x < -100 || comet.x > width + 100 || comet.y < -100 || comet.y > height + 100) {
            continue;
          }

          const tailEndX = comet.x - Math.cos(comet.angle) * comet.tailLength;
          const tailEndY = comet.y - Math.sin(comet.angle) * comet.tailLength;
          const tailGradient = ctx.createLinearGradient(comet.x, comet.y, tailEndX, tailEndY);
          tailGradient.addColorStop(0, "rgba(150, 200, 255, 0.8)");
          tailGradient.addColorStop(0.3, "rgba(100, 150, 255, 0.4)");
          tailGradient.addColorStop(1, "rgba(100, 150, 255, 0)");

          ctx.strokeStyle = tailGradient;
          ctx.lineWidth = comet.size * 2;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(comet.x, comet.y);
          ctx.lineTo(tailEndX, tailEndY);
          ctx.stroke();

          ctx.fillStyle = "rgba(180, 215, 255, 0.85)";
          ctx.beginPath();
          ctx.arc(comet.x, comet.y, comet.size * 1.5, 0, Math.PI * 2);
          ctx.fill();

          alive.push(comet);
        }
        comets = alive;

        if (Math.random() < 0.002 && comets.length < budget.maxComets) {
          comets.push(createComet());
        }
      }

      animationId = requestAnimationFrame(draw);
    };

    const start = () => {
      if (running) return;
      running = true;
      lastFrameTime = 0;
      animationId = requestAnimationFrame(draw);
    };

    const stop = () => {
      running = false;
      if (animationId) cancelAnimationFrame(animationId);
    };

    // Pausa com a aba/app em segundo plano — o loop antigo queimava
    // bateria desenhando um céu que ninguém estava vendo.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") start();
      else stop();
    };

    const handlePointer = (e: PointerEvent) => {
      pointer.x = e.clientX / window.innerWidth;
      pointer.y = e.clientY / window.innerHeight;
    };

    resize();
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", handleVisibility);
    if (budget.parallax && !reducedMotion) {
      window.addEventListener("pointermove", handlePointer, { passive: true });
    }

    animationId = requestAnimationFrame(draw);

    return () => {
      stop();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pointermove", handlePointer);
      staticLayer = null;
    };
  }, [tier, reducedMotion]);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }} aria-hidden="true" />;
});
